import { useCallback, useEffect, useRef, useState, type MutableRefObject, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import { BarraDeCarga } from './BarraDeCarga';
import { paginaMasVisible } from '../lecturas/paginas';

/** A qué página desplazarse. `vez` distingue dos pedidos de la misma página. */
export interface Objetivo {
  pagina: number;
  vez: number;
}

/** Proporción de una hoja carta, para reservar el lugar de las que no han llegado. */
const CARTA = 8.5 / 11;

/**
 * Cuánto se adelanta la carga: hasta pantalla y media por arriba y por abajo. Lo
 * bastante para que al desplazarse la página ya esté ahí, y lo bastante poco
 * para no pedir de golpe un manual de 300 páginas, cada una con su marca de
 * agua compuesta en el servidor.
 */
const MARGEN_DE_CARGA = '150% 0px 150% 0px';

/**
 * Pantalla completa: todas las páginas, una debajo de otra.
 *
 * Antes en pantalla completa también se pasaba página por página, con el botón.
 * Ahora se desplaza, y cada página se pide al acercarse y se suelta al alejarse
 * —el lienzo se vacía—: con el documento entero en memoria, un manual largo
 * tumbaba la pestaña.
 *
 * La página «a la vista» es la que más alto ocupa de la pantalla: es la que
 * cuenta para el tiempo de lectura y la que muestra el campo de la página.
 */
export function PaginasContinuas({
  raiz,
  concesion,
  total,
  zoom,
  anchoMaximo,
  objetivo,
  leidas,
  onActual,
  onError,
}: {
  /** El contenedor que se desplaza. */
  raiz: RefObject<HTMLDivElement>;
  /** La concesión vigente; cambia cada 40 s y no debe hacer recargar nada. */
  concesion: MutableRefObject<string | null>;
  total: number;
  zoom: number;
  anchoMaximo: number;
  objetivo: Objetivo;
  leidas: ReadonlySet<number>;
  onActual: (pagina: number, dibujada: boolean) => void;
  onError: (error: unknown) => void;
}) {
  const [cerca, setCerca] = useState<ReadonlySet<number>>(new Set());
  const [proporcion, setProporcion] = useState(CARTA);
  const hojas = useRef(new Map<number, HTMLDivElement>());
  const alturas = useRef(new Map<number, number>());
  const dibujadas = useRef(new Set<number>());
  const actual = useRef<number | null>(null);
  const avisar = useRef(onActual);
  avisar.current = onActual;

  // Dos observadores: uno decide qué páginas se cargan (las cercanas), el otro
  // cuál está a la vista (la que más pantalla ocupa).
  useEffect(() => {
    const contenedor = raiz.current;
    if (!contenedor) return;

    const cercania = new IntersectionObserver(
      (entradas) => {
        setCerca((anteriores) => {
          const siguientes = new Set(anteriores);
          for (const entrada of entradas) {
            const numero = Number((entrada.target as HTMLElement).dataset.pagina);
            if (entrada.isIntersecting) siguientes.add(numero);
            else siguientes.delete(numero);
          }
          return siguientes;
        });
      },
      { root: contenedor, rootMargin: MARGEN_DE_CARGA },
    );

    const visibilidad = new IntersectionObserver(
      (entradas) => {
        for (const entrada of entradas) {
          const numero = Number((entrada.target as HTMLElement).dataset.pagina);
          alturas.current.set(numero, entrada.isIntersecting ? entrada.intersectionRect.height : 0);
        }
        const mejor = paginaMasVisible(alturas.current);
        if (mejor !== null && mejor !== actual.current) {
          actual.current = mejor;
          avisar.current(mejor, dibujadas.current.has(mejor));
        }
      },
      { root: contenedor, threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1] },
    );

    for (const hoja of hojas.current.values()) {
      cercania.observe(hoja);
      visibilidad.observe(hoja);
    }
    return () => {
      cercania.disconnect();
      visibilidad.disconnect();
    };
  }, [raiz, total]);

  // Ir a una página pedida desde fuera: el campo, las flechas, «siguiente sin leer».
  useEffect(() => {
    hojas.current.get(objetivo.pagina)?.scrollIntoView({ block: 'start' });
  }, [objetivo]);

  const alDibujar = useCallback((numero: number, relacion: number) => {
    dibujadas.current.add(numero);
    setProporcion((anterior) => (anterior === CARTA ? relacion : anterior));
    if (actual.current === numero) avisar.current(numero, true);
  }, []);

  const alSoltar = useCallback((numero: number) => {
    dibujadas.current.delete(numero);
    if (actual.current === numero) avisar.current(numero, false);
  }, []);

  const registrar = useCallback((numero: number, elemento: HTMLDivElement | null) => {
    if (elemento) hojas.current.set(numero, elemento);
    else hojas.current.delete(numero);
  }, []);

  return (
    <div className="visor__continuo">
      {Array.from({ length: total }, (_, i) => i + 1).map((numero) => (
        <Hoja
          key={numero}
          numero={numero}
          cargar={cerca.has(numero)}
          concesion={concesion}
          zoom={zoom}
          anchoMaximo={anchoMaximo}
          proporcion={proporcion}
          leida={leidas.has(numero)}
          registrar={registrar}
          onDibujada={alDibujar}
          onSoltada={alSoltar}
          onError={onError}
        />
      ))}
    </div>
  );
}

function Hoja({
  numero,
  cargar,
  concesion,
  zoom,
  anchoMaximo,
  proporcion,
  leida,
  registrar,
  onDibujada,
  onSoltada,
  onError,
}: {
  numero: number;
  cargar: boolean;
  concesion: MutableRefObject<string | null>;
  zoom: number;
  anchoMaximo: number;
  proporcion: number;
  leida: boolean;
  registrar: (numero: number, elemento: HTMLDivElement | null) => void;
  onDibujada: (numero: number, relacion: number) => void;
  onSoltada: (numero: number) => void;
  onError: (error: unknown) => void;
}) {
  const { t } = useTranslation();
  const lienzo = useRef<HTMLCanvasElement>(null);
  const [dibujada, setDibujada] = useState(false);
  const [avance, setAvance] = useState<number | null>(null);
  /** La proporción de esta página, una vez vista: su lugar no cambia al soltarla. */
  const [relacion, setRelacion] = useState<number | null>(null);

  useEffect(() => {
    const canvas = lienzo.current;
    if (!cargar) {
      // Se suelta: el lienzo vacío no ocupa memoria; el lugar lo guarda la proporción.
      if (canvas) {
        canvas.width = 0;
        canvas.height = 0;
      }
      setDibujada(false);
      onSoltada(numero);
      return;
    }
    const grantId = concesion.current;
    if (!grantId) return;
    const cancelar = new AbortController();
    setAvance(null);
    (async () => {
      try {
        const blob = await api.viewerPage(grantId, numero, zoom, setAvance, cancelar.signal);
        const url = URL.createObjectURL(blob);
        try {
          const imagen = await cargarImagen(url);
          if (!lienzo.current || cancelar.signal.aborted) return;
          lienzo.current.width = imagen.naturalWidth;
          lienzo.current.height = imagen.naturalHeight;
          lienzo.current.getContext('2d')?.drawImage(imagen, 0, 0);
          const propia = imagen.naturalWidth / imagen.naturalHeight;
          setRelacion(propia);
          setDibujada(true);
          onDibujada(numero, propia);
        } finally {
          URL.revokeObjectURL(url);
        }
      } catch (fallo) {
        if (!cancelar.signal.aborted) onError(fallo);
      }
    })();
    return () => cancelar.abort();
    // `concesion` es un ref a propósito: renovarla no vuelve a pedir la página.
  }, [cargar, zoom, numero, concesion, onDibujada, onSoltada, onError]);

  return (
    <div
      ref={(elemento) => registrar(numero, elemento)}
      data-pagina={numero}
      className="visor__hoja"
      style={{ width: `min(100%, ${anchoMaximo}px)`, aspectRatio: String(relacion ?? proporcion) }}
    >
      {!dibujada && (
        <BarraDeCarga avance={cargar ? avance : null} texto={t('viewer.loadingPage', { page: numero })} />
      )}
      <canvas
        ref={lienzo}
        className={`visor__pagina${dibujada ? '' : ' visor__pagina--oculta'}`}
        onContextMenu={(evento) => evento.preventDefault()}
        aria-label={t('viewer.pageNumber', { page: numero })}
      />
      <span className={`visor__hoja-numero${leida ? ' visor__hoja-numero--leida' : ''}`}>
        {leida ? t('viewer.pageReadMark', { page: numero }) : numero}
      </span>
    </div>
  );
}

export function cargarImagen(url: string): Promise<HTMLImageElement> {
  return new Promise((resolver, rechazar) => {
    const imagen = new Image();
    imagen.onload = () => resolver(imagen);
    imagen.onerror = () => rechazar(new Error('viewer.image_failed'));
    imagen.src = url;
  });
}
